import { Collapse, Divider, List } from '@material-ui/core';
import { useLoggedInUser } from 'app/auth/useLoggedInUser';
import { useStateToggleBoolean } from 'framework/hooks/useStateToggleBoolean';
import { toggleSelect } from 'framework/utils/array/toggleSelect';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { CalendarFilterListItemComponent } from './CalendarFilterListItemComponent';
import { CalendarFilterListItemHeaderComponent } from './CalendarFilterListItemHeaderComponent';
import { ICalendarResource } from './ICalendarResource';

interface IProps {
	data: ICalendarResource[];
	selectedKeys: string[];
	setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
	isColorSchemeSelected: boolean;
	selectColorScheme: VoidFunction;
	showSecondaryColors?: boolean;
	isGroupSelected: boolean;
	selectGroup: VoidFunction;
	canGroup: boolean;
	style?: React.CSSProperties;
	isDefaultExpanded?: boolean;
}

export const CalendarUsersFilterListComponent = ({
	data,
	selectedKeys,
	setSelectedKeys,
	isColorSchemeSelected,
	selectColorScheme,
	isGroupSelected,
	selectGroup,
	canGroup,
	style,
	showSecondaryColors,
	isDefaultExpanded,
}: IProps) => {
	const strings = useLocalization();
	const [isExpanded, toggle] = useStateToggleBoolean(isDefaultExpanded ?? false);
	const isAllSelected = useMemo(() => data.length === selectedKeys.length, [data, selectedKeys]);
	const isNoneSelected = useMemo(() => selectedKeys.length === 0, [selectedKeys]);
	const loggedInUser = useLoggedInUser();
	const loggedInUserResource = useMemo(() => data.find(t => t.key === loggedInUser.id), [loggedInUser, data]);
	const isOnlyLoggedInUserSelected = useMemo(
		() => selectedKeys.length === 1 && selectedKeys[0] === loggedInUserResource?.key,
		[selectedKeys, loggedInUserResource]
	);

	return (
		<div
			className='df-col'
			style={style}>
			<CalendarFilterListItemHeaderComponent
				title={strings.users}
				isColorSchemeSelected={isColorSchemeSelected}
				isExpanded={isExpanded}
				isGroupSelected={isGroupSelected}
				onClick={toggle}
				selectColorScheme={selectColorScheme}
				selectGroup={selectGroup}
				hideGroup={canGroup === false}
			/>
			<Collapse
				in={isExpanded}
				timeout='auto'
				unmountOnExit>
				{loggedInUserResource === undefined && (
					<CalendarFilterListItemComponent
						isSelected={isAllSelected}
						indeterminate={isNoneSelected === false && isAllSelected === false}
						text={isAllSelected ? strings.selectNone : strings.selectAll}
						onClick={() => (isAllSelected ? setSelectedKeys([]) : setSelectedKeys(data.map(t => t.key)))}
						color='unset'
						showPrimaryColor={false}
						showSecondaryColor={false}
					/>
				)}
				{loggedInUserResource !== undefined && (
					<CalendarFilterListItemComponent
						isSelected={isAllSelected}
						indeterminate={isNoneSelected === false && isAllSelected === false}
						text={
							isAllSelected
								? strings.selectOnlyWho(`'${loggedInUser.lastName}, ${loggedInUser.firstName}'`)
								: isOnlyLoggedInUserSelected
								? strings.selectNone
								: strings.selectAll
						}
						onClick={() =>
							isAllSelected
								? setSelectedKeys([loggedInUserResource.key])
								: isOnlyLoggedInUserSelected
								? setSelectedKeys([])
								: setSelectedKeys(data.map(t => t.key))
						}
						color='unset'
						showPrimaryColor={false}
						showSecondaryColor={false}
					/>
				)}
				<List
					dense
					disablePadding>
					<Divider variant='middle' />
					{data.map(t => (
						<CalendarFilterListItemComponent
							onClick={() => setSelectedKeys(toggleSelect(t.key, selectedKeys))}
							isSelected={selectedKeys.indexOf(t.key) !== -1}
							color={t.color}
							text={t.isEmpty ? <em>{t.name}</em> : t.name}
							key={t.key}
							showPrimaryColor={isColorSchemeSelected}
							showSecondaryColor={showSecondaryColors ?? false}
						/>
					))}
				</List>
			</Collapse>
		</div>
	);
};
